import Seo from "../../components/seo";
import Layout from "../../components/layout";
import Header from "../../components/header";
import Footer from "../../components/footer";
import ServiceHero from "../../components/ServiceHero";
import ServiceCTA from "../../components/ServiceCTA";
import { CardColumn, InfoCard } from "../../components/sections";
import { FiTarget, FiCpu, FiBarChart2, FiCode, FiTrendingUp } from 'react-icons/fi';
import { usePageMetadata } from "../../hooks/usePageMetadata";

const AIMLPage = () => {
  usePageMetadata({
    title: 'AI/ML & Generative AI - Bercastle',
    description: 'Custom AI copilots, machine learning models, and generative AI solutions for your business.',
    pageType: 'service',
    content: {
      service: {
        name: "AI/ML & Generative AI",
        tagline: "Next-Generation AI Solutions",
        description: "Custom AI copilots, machine learning models, and generative AI solutions that transform how you work.",
        url: "/services/ai-ml-generative"
      },
      capabilities: [
        {
          name: "Custom AI Copilot",
          description: "Build intelligent assistants that enhance productivity and automate complex tasks.",
          features: ["Natural language understanding", "Context-aware responses", "Multi-turn conversations", "Integration with your systems", "Custom knowledge bases", "Continuous learning"],
          use_cases: ["Customer support automation", "Internal knowledge assistant", "Code generation helper", "Content creation tool", "Data analysis assistant"]
        },
        {
          name: "Machine Learning Solutions",
          description: "Custom ML models for prediction, classification, and pattern recognition.",
          capabilities: ["Predictive analytics", "Recommendation systems", "Fraud detection", "Demand forecasting", "Customer segmentation", "Anomaly detection"]
        },
        {
          name: "Generative AI Applications",
          description: "Leverage the latest generative AI models for content creation and automation.",
          applications: ["Text generation", "Image synthesis", "Code generation", "Data augmentation", "Creative content", "Automated summarization"]
        },
        {
          name: "Large Language Model Integration",
          description: "Integrate and fine-tune LLMs for your specific use cases.",
          services: ["Model selection & evaluation", "Fine-tuning & customization", "Prompt engineering", "RAG implementation", "Vector databases", "Cost optimization"]
        }
      ],
      technologies: ["OpenAI GPT-4, GPT-3.5", "Anthropic Claude", "Google Gemini", "Meta LLaMA", "Hugging Face models", "LangChain, LlamaIndex", "Pinecone, Weaviate"],
      process: ["Discovery", "Data Assessment", "Model Selection", "Development", "Testing", "Deployment", "Monitoring"],
      value_propositions: [
        "Cutting-edge technology",
        "Domain expertise",
        "Production-ready solutions",
        "Ethical AI practices",
        "Scalable architecture",
        "Comprehensive support"
      ],
      meta: {
        source_url: "https://bercastle.com/services/ai-ml-generative",
        service_category: "AI/ML & Generative AI"
      }
    }
  });
  const serviceCards = [
    {
      title: "Custom AI Copilot",
      description: (
        <ul className='space-y-2'>
          <li className='flex items-start gap-2'>
            <span className='text-brand-orange mt-1'>•</span>
            <span>Tailored AI assistants for specific business operations (finance, legal, HR)</span>
          </li>
          <li className='flex items-start gap-2'>
            <span className='text-brand-orange mt-1'>•</span>
            <span>Integration with internal tools (Slack, Microsoft 365, CRMs)</span>
          </li>
          <li className='flex items-start gap-2'>
            <span className='text-brand-orange mt-1'>•</span>
            <span>Context-aware, role-specific assistants</span>
          </li>
        </ul>
      ),
    },
    {
      title: "RAG Systems",
      description: (
        <ul className='space-y-2'>
          <li className='flex items-start gap-2'>
            <span className='text-brand-orange mt-1'>•</span>
            <span>Building LLM apps that combine real-time data search with AI response</span>
          </li>
          <li className='flex items-start gap-2'>
            <span className='text-brand-orange mt-1'>•</span>
            <span>Used in customer support, internal knowledge bases, and legal tech</span>
          </li>
          <li className='flex items-start gap-2'>
            <span className='text-brand-orange mt-1'>•</span>
            <span>Semantic search and contextual question answering</span>
          </li>
        </ul>
      ),
    },
    {
      title: "Private LLM Deployment",
      description: (
        <ul className='space-y-2'>
          <li className='flex items-start gap-2'>
            <span className='text-brand-orange mt-1'>•</span>
            <span>On-premise or private cloud deployment of open-source models</span>
          </li>
          <li className='flex items-start gap-2'>
            <span className='text-brand-orange mt-1'>•</span>
            <span>Security and compliance-focused (healthcare, finance, legal)</span>
          </li>
          <li className='flex items-start gap-2'>
            <span className='text-brand-orange mt-1'>•</span>
            <span>Full control over data and model behavior</span>
          </li>
        </ul>
      ),
    },
    {
      title: "Finetuning & Customization",
      description: (
        <ul className='space-y-2'>
          <li className='flex items-start gap-2'>
            <span className='text-brand-orange mt-1'>•</span>
            <span>Fine-tuning open-source models on proprietary data</span>
          </li>
          <li className='flex items-start gap-2'>
            <span className='text-brand-orange mt-1'>•</span>
            <span>LoRA, QLoRA, and full fine-tuning of LLMs</span>
          </li>
          <li className='flex items-start gap-2'>
            <span className='text-brand-orange mt-1'>•</span>
            <span>Domain-specific model training (legal, finance, medical)</span>
          </li>
        </ul>
      ),
    },
  ];

  const techCards = [
    {
      title: "LLMs",
      description: (
        <div className='space-y-2'>
          <p><strong>Open-source:</strong> LLaMA, Mistral, DeepSeek</p>
          <p><strong>Commercial:</strong> OpenAI GPT-4, Claude, Gemini</p>
        </div>
      ),
    },
    {
      title: "Frameworks",
      description: (
        <div className='space-y-1'>
          <p>• PyTorch, TensorFlow</p>
          <p>• HuggingFace Transformers</p>
          <p>• LangChain, LlamaIndex</p>
          <p>• Fast Agent</p>
        </div>
      ),
    },
    {
      title: "Vector DBs",
      description: (
        <div className='space-y-1'>
          <p>• Qdrant, Pinecone</p>
          <p>• pgVector</p>
          <p>• Elasticsearch</p>
          <p>• Neo4j (graph)</p>
        </div>
      ),
    },
    {
      title: "Deployment",
      description: (
        <div className='space-y-1'>
          <p>• Docker, Kubernetes</p>
          <p>• AWS SageMaker</p>
          <p>• Azure AI, GCP Vertex</p>
          <p>• NVIDIA Triton</p>
        </div>
      ),
    },
    {
      title: "Speech & Vision",
      description: (
        <div className='space-y-1'>
          <p>• Whisper, ElevenLabs</p>
          <p>• OpenCV, YOLO</p>
          <p>• Mediapipe</p>
          <p>• DeepSpeech</p>
        </div>
      ),
    },
    {
      title: "Languages",
      description: (
        <div className='space-y-1'>
          <p>• Python (primary)</p>
          <p>• JavaScript/TypeScript</p>
          <p>• C++, Rust</p>
          <p>• Go, Java</p>
        </div>
      ),
    },
  ];

  const processSteps = [
    { 
      icon: <FiTarget size={24} />,
      title: 'Discovery', 
      description: 'Understand your business challenges and opportunities' 
    },
    { 
      icon: <FiCpu size={24} />,
      title: 'Model Selection', 
      description: 'Choose the right AI/ML approach for your needs' 
    },
    { 
      icon: <FiBarChart2 size={24} />,
      title: 'Strategy', 
      description: 'Craft clear AI integration strategy and roadmap' 
    },
    { 
      icon: <FiCode size={24} />,
      title: 'POC & MVP', 
      description: 'Build proof of concept and minimum viable product' 
    },
    { 
      icon: <FiTrendingUp size={24} />,
      title: 'Development', 
      description: 'Full-scale development and deployment' 
    }
  ];

  return (
    <Layout classmain="ai-ml-page">
      <Seo
        title='AI/ML & Generative AI - OnScript AI'
        metaDesciption='Enterprise AI/ML and generative AI development services. Custom AI copilots, RAG systems, private LLM deployment, and fine-tuning for your business.'
      />
      <Header />

      <ServiceHero 
        badge="AI/ML & Generative AI"
        title="Turn Smart Ideas Into"
        titleHighlight="Smarter Products"
        description="We help you move faster, work smarter, and deliver more value through AI/ML and generative AI solutions tailored to your business needs."
        ctaLink="/contact"
      />

      <div className='py-20'>
        <div className='container-custom'>
          <CardColumn
            title="Our AI/ML Services"
            description="Comprehensive AI/ML development services from concept to deployment"
            cards={serviceCards}
            cols={2}
            className='w-full'
          />
        </div>
      </div>

      <div className='py-20'>
        <div className='container-custom'>
          <CardColumn
            title="Technologies We Use"
            description="Cutting-edge AI/ML tools and frameworks"
            cards={techCards}
            cols={3}
            className='w-full'
          />
        </div>
      </div>

      <div className='py-20'>
        <div className='container-custom'>
          <CardColumn
            title="Our AI Consulting Process"
            description="From discovery to deployment and beyond"
            cards={processSteps}
            cols={5}
            className='w-full'
          />
        </div>
      </div>

      <ServiceCTA 
        title="Ready to Transform Your Business with AI?"
        description="Let's build intelligent solutions that deliver measurable results for your organization."
        buttonText="Start Your AI Project"
      />

      <Footer />
    </Layout>
  )
}

export default AIMLPage
